import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LocalDeviceMetadataUpdate } from '@mytag/shared';
import { mytagApi } from '@/services/mytagApi';

/**
 * Polling léger, configurable, de la liste des appareils partagée par le
 * dashboard web et l'APK. 0/valeur invalide désactive l'intervalle (React
 * Query se met alors uniquement à jour sur invalidation explicite ou retour
 * au premier plan). Relit seulement getDeviceList côté backend : ne
 * déclenche jamais de refreshLocation Vernal.
 */
const DEVICES_POLL_MS = Number(import.meta.env.VITE_DEVICES_POLL_MS ?? 30_000) || 0;

/** Clés de cache centralisées : une seule source de vérité pour l'invalidation. */
export const queryKeys = {
  health: ['health'] as const,
  dashboard: ['dashboard'] as const,
  devices: ['devices'] as const,
  device: (deviceNum: string) => ['device', deviceNum] as const,
  locations: (deviceNum: string, from?: string, to?: string, limit?: number) =>
    ['locations', deviceNum, from ?? null, to ?? null, limit ?? null] as const,
  debugLogs: (limit: number) => ['debugLogs', limit] as const,
  debugConfig: ['debugConfig'] as const,
  capabilities: ['capabilities'] as const,
  alerts: ['alerts'] as const, geofences: ['geofences'] as const, reports: ['reports'] as const,
};

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: mytagApi.health,
    refetchInterval: 60_000,
    retry: 1,
  });
}

export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: mytagApi.dashboard,
    enabled,
    refetchInterval: DEVICES_POLL_MS || false,
  });
}

export function useDevices(enabled = true, pageNo=1, pageSize=20) {
  return useQuery({
    queryKey: [...queryKeys.devices,pageNo,pageSize],
    queryFn: ()=>mytagApi.devices(pageNo,pageSize),
    enabled,
    refetchInterval: DEVICES_POLL_MS || false,
  });
}

export function useDevice(deviceNum: string | undefined) {
  return useQuery({
    queryKey: queryKeys.device(deviceNum ?? ''),
    queryFn: () => mytagApi.device(deviceNum as string),
    enabled: Boolean(deviceNum),
  });
}

export function useLocations(
  deviceNum: string | undefined,
  params: { from?: string; to?: string; limit?: number } = {},
) {
  return useQuery({
    queryKey: queryKeys.locations(deviceNum ?? '', params.from, params.to, params.limit),
    queryFn: () => mytagApi.locations(deviceNum as string, params),
    enabled: Boolean(deviceNum),
  });
}

/** Invalide tout ce qui dépend de l'état des devices après une action. */
function useDeviceInvalidation() {
  const queryClient = useQueryClient();
  return (deviceNum?: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.devices });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    if (deviceNum) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.device(deviceNum) });
      void queryClient.invalidateQueries({ queryKey: ['locations', deviceNum] });
    }
    void queryClient.invalidateQueries({ queryKey: ['debugLogs'] });
  };
}

export function useRefreshLocation() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: (deviceNum: string) => mytagApi.refreshLocation(deviceNum),
    onSuccess: (result) => invalidate(result.deviceNum),
  });
}

export function useRefreshAll() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: (deviceNums: string[]) => mytagApi.refreshLocations(deviceNums, true),
    onSuccess: () => { invalidate(); window.setTimeout(()=>invalidate(),2500); },
  });
}

/** Pilote l'UI Associer/Dissocier : ne jamais deviner ALLOW_REAL_BIND_UNBIND côté client. */
export function useCapabilities() {
  return useQuery({ queryKey: queryKeys.capabilities, queryFn: mytagApi.capabilities });
}

export function useBindDevice() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: ({ deviceNum, name, confirmReal }: { deviceNum: string; name?: string; confirmReal?: boolean }) =>
      mytagApi.bind(deviceNum, name, confirmReal),
    onSuccess: (result) => invalidate(result.deviceNum),
  });
}

export function useUnbindDevice() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: ({ deviceNum, confirmReal }: { deviceNum: string; confirmReal?: boolean }) =>
      mytagApi.unbind(deviceNum, confirmReal),
    onSuccess: (result) => invalidate(result.deviceNum),
  });
}

export function useUpdateDeviceMetadata() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: ({ deviceNum, update }: { deviceNum: string; update: LocalDeviceMetadataUpdate }) =>
      mytagApi.updateDeviceMetadata(deviceNum, update),
    onSuccess: (_result, variables) => invalidate(variables.deviceNum),
  });
}

export function useArchiveDevice() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: (deviceNum: string) => mytagApi.archiveDevice(deviceNum),
    onSuccess: (_result, deviceNum) => invalidate(deviceNum),
  });
}

export function useRestoreDevice() {
  const invalidate = useDeviceInvalidation();
  return useMutation({
    mutationFn: (deviceNum: string) => mytagApi.restoreDevice(deviceNum),
    onSuccess: (_result, deviceNum) => invalidate(deviceNum),
  });
}

export function useDebugLogs(limit = 100) {
  return useQuery({
    queryKey: queryKeys.debugLogs(limit),
    queryFn: () => mytagApi.debugLogs(limit),
    refetchInterval: 15_000,
  });
}

export function useDebugConfig() {
  return useQuery({ queryKey: queryKeys.debugConfig, queryFn: mytagApi.debugConfig });
}
export function useAlerts() { return useQuery({queryKey:queryKeys.alerts,queryFn:mytagApi.alerts,refetchInterval:30_000}); }
export function useUpdateAlert() { const qc=useQueryClient(); return useMutation({mutationFn:({id,status}:{id:string;status:'read'|'resolved'})=>mytagApi.updateAlert(id,status),onSuccess:()=>void qc.invalidateQueries({queryKey:queryKeys.alerts})}); }
export function useGeofences() { return useQuery({queryKey:queryKeys.geofences,queryFn:mytagApi.geofences}); }
export function useSaveGeofence() { const qc=useQueryClient(); return useMutation({mutationFn:(value:Parameters<typeof mytagApi.saveGeofence>[0])=>mytagApi.saveGeofence(value),onSuccess:()=>void qc.invalidateQueries({queryKey:queryKeys.geofences})}); }
export function useDeleteGeofence() { const qc=useQueryClient(); return useMutation({mutationFn:mytagApi.deleteGeofence,onSuccess:()=>void qc.invalidateQueries({queryKey:queryKeys.geofences})}); }
export function useReports() { return useQuery({queryKey:queryKeys.reports,queryFn:mytagApi.reports}); }
export function useCreateReport() { const qc=useQueryClient(); return useMutation({mutationFn:mytagApi.createReport,onSuccess:()=>void qc.invalidateQueries({queryKey:queryKeys.reports})}); }
